import React, { useEffect, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  Table,
} from 'reactstrap';
import moment from 'moment';
import {
  fetchNotificationQueueList,
  fetchNotificationQueueSummary,
} from '../action/action';
import DHPagination from '../../../../components/Pagination';

const STATES = [
  { key: 'waiting', label: 'Waiting' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
  { key: 'delayed', label: 'Delayed' },
];

const STATE_BADGE_COLOR = {
  waiting: 'secondary',
  active: 'info',
  completed: 'success',
  failed: 'danger',
  delayed: 'warning',
};

const formatTs = (ts) => (ts ? moment(Number(ts)).format('Do MMM YYYY h:mmA') : '-');

const RedisQueueList = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [activeState, setActiveState] = useState('waiting');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const setPaginationValue = ({ page: p }) => setPage(Number(p));
  const setLimitValue = ({ limit: l }) => {
    setLimit(Number(l));
    setPage(0);
  };

  const getQueueList = useCallback(
    async (params) => {
      try {
        setLoading(true);
        await fetchNotificationQueueList({ params, dispatch });
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    getQueueList({
      pagination: { page: page + 1, limit },
      filter: { state: activeState },
    });
  }, [activeState, page, limit, getQueueList]);

  useEffect(() => {
    fetchNotificationQueueSummary({ dispatch });
  }, [dispatch]);

  const { queueList, queueSummary } = useSelector(
    (reduxState) => reduxState.notificationManager,
  );
  const items = queueList?.items || [];
  const totalPages = queueList?.pageInfo?.totalPages || 0;

  const toggleState = (key) => {
    if (activeState !== key) {
      setActiveState(key);
      setPage(0);
    }
  };

  return (
    <Card className="mt-0" style={{ borderTopLeftRadius: 0 }}>
      <CardBody>
        <Nav pills className="mb-3 flex-wrap gap-2">
          {STATES.map((s) => (
            <NavItem key={s.key}>
              <NavLink
                className={classnames(
                  { active: activeState === s.key },
                  'd-flex align-items-center gap-1',
                )}
                onClick={() => toggleState(s.key)}
                style={{ cursor: 'pointer' }}
              >
                {s.label}
                <Badge color="light" className="text-dark">
                  {queueSummary?.[s.key] ?? 0}
                </Badge>
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <div className="table-responsive dt-table">
          <Table className="table mb-0">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Order Reference</th>
                <th>Status</th>
                <th>State</th>
                <th>Attempts</th>
                <th>Queued At</th>
                <th>Finished At</th>
                <th>Failure Reason</th>
              </tr>
            </thead>
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <Spinner color="primary" />
                  </td>
                </tr>
              </tbody>
            ) : items.length > 0 ? (
              <tbody>
                {items.map((job) => (
                  <tr key={job.id}>
                    <td>{job.id}</td>
                    <td>{job.order_reference}</td>
                    <td>{job.status}</td>
                    <td>
                      <Badge color={STATE_BADGE_COLOR[job.state] || 'secondary'}>
                        {job.state}
                      </Badge>
                    </td>
                    <td>{job.attemptsMade}</td>
                    <td nowrap="nowrap">{formatTs(job.timestamp)}</td>
                    <td nowrap="nowrap">{formatTs(job.finishedOn)}</td>
                    <td>{job.failedReason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={8}>No jobs found</td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>

        {items.length > 0 ? (
          <DHPagination
            totalPages={totalPages}
            currentPage={page}
            setPaginationValue={setPaginationValue}
            setLimitValue={setLimitValue}
          />
        ) : null}
      </CardBody>
    </Card>
  );
};

export default RedisQueueList;
